import { AccountRegions } from '@payaca/types/accountTypes';
import {
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumber,
  PhoneNumber,
} from 'libphonenumber-js';

export const formatPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return '';
  if (phoneNumber.length < 2) return phoneNumber;

  const firstCharacters = phoneNumber.substring(0, 2);
  if (firstCharacters === '44') {
    phoneNumber = '+' + phoneNumber;
  }
  try {
    const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
    if (parsedPhoneNumber) {
      return parsedPhoneNumber.formatInternational();
    }
  } catch (error) {
    return phoneNumber;
  }

  return phoneNumber;
};

export const getParsedPhoneNumber = (
  phoneNumber: string,
  defaultCountry?: CountryCode
): PhoneNumber | null => {
  if (!phoneNumber) return null;
  if (phoneNumber.length < 2) return null;

  const firstCharacters = phoneNumber.substring(0, 2);
  if (firstCharacters === '44') {
    phoneNumber = '+' + phoneNumber;
  }
  try {
    return parsePhoneNumber(phoneNumber, defaultCountry);
  } catch (error) {
    return null;
  }
};

export const convertToInternationalPhoneNumber = (
  accountRegion: AccountRegions,
  phoneNumber: string
) => {
  let dialingCode = '+44';
  switch (accountRegion) {
    case AccountRegions.AUSTRALIA:
      dialingCode = '+61';
      break;
    case AccountRegions.SOUTH_AFRICA:
      dialingCode = '+27';
      break;
    case AccountRegions.CANADA:
    // fall-through
    case AccountRegions.US:
      dialingCode = '+1';
      break;
  }
  if (phoneNumber.startsWith(dialingCode)) {
    return phoneNumber;
  }
  if (phoneNumber.startsWith(dialingCode.slice(1))) {
    return `+${phoneNumber}`;
  }
  return `${dialingCode}${phoneNumber.slice(1)}`;
};

const ACCOUNT_REGION_COUNTRY: Record<AccountRegions, CountryCode> = {
  [AccountRegions.AUSTRALIA]: 'AU',
  [AccountRegions.CANADA]: 'CA',
  [AccountRegions.NEW_ZEALAND]: 'NZ',
  [AccountRegions.SOUTH_AFRICA]: 'ZA',
  // libphonenumber uses the ISO country code GB, not the AccountRegions value 'uk'.
  [AccountRegions.UK]: 'GB',
  [AccountRegions.US]: 'US',
};

/**
 * Maps an account region to the libphonenumber country code used to parse and
 * dial its numbers. Returns `undefined` when no region is known, in which case
 * a domestic number cannot be assigned a calling code.
 */
export const accountRegionToCountryCode = (
  region: AccountRegions | null | undefined
): CountryCode | undefined =>
  region ? ACCOUNT_REGION_COUNTRY[region] : undefined;

const MIN_PHONE_SEARCH_DIGITS = 5;

/**
 * Whether a search term has enough digits to be treated as a phone-number
 * search. Lets callers cheaply decide — before any region lookup that full
 * normalisation needs — whether the phone path is worth taking at all.
 */
export const looksLikePhoneSearch = (searchTerm: string): boolean =>
  searchTerm.replace(/\D/g, '').length >= MIN_PHONE_SEARCH_DIGITS;

/**
 * Reduces any telephone input to a normalised key of country calling code
 * followed by the national significant digits (E.164 without the leading `+`),
 * used identically for stored values and search queries so the two always agree.
 *
 * Rules, applied in order:
 *  1. A valid complete number → its E.164 digits.
 *  2. Explicit international form (leading `+` or `00`) → the digits, with the
 *     `+`/`00` dropped (the calling code is already present).
 *  3. Domestic form, when a region is known → strip a single leading trunk `0`
 *     and prepend the region's calling code.
 *  4. Otherwise (no region, no explicit calling code) → digits only.
 *
 * Returns `null` when the input has no digits. Never throws: malformed or abused
 * free-text input (embedded names, multiple numbers) falls through to a
 * best-effort key rather than raising.
 */
export const normalisePhoneNumber = (
  phoneNumber: string,
  region?: AccountRegions | null
): string | null => {
  if (!phoneNumber) return null;

  const country = accountRegionToCountryCode(region);

  // Rule 1: a number the library recognises as valid is authoritative.
  const parsed = getParsedPhoneNumber(phoneNumber, country);
  if (parsed?.isValid()) {
    return parsed.number.replace('+', '');
  }

  const digits = phoneNumber.replace(/\D/g, '');
  if (!digits) return null;

  // Rule 2: explicit international form already carries its calling code.
  if (phoneNumber.trim().startsWith('+')) {
    return digits;
  }
  if (digits.startsWith('00')) {
    return digits.slice(2) || null;
  }

  // Rule 3: a domestic number is interpreted in the account's region.
  if (country) {
    return `${getCountryCallingCode(country)}${digits.replace(/^0/, '')}`;
  }

  // Rule 4: no country can be determined.
  return digits;
};

/**
 * Normalises a customer-search term for matching against stored numbers,
 * returning `null` when the term has too few digits to be a plausible phone
 * number so that ordinary name/word searches are not treated as phone searches.
 */
export const normalisePhoneSearch = (
  searchTerm: string,
  region?: AccountRegions | null
): string | null => {
  if (!looksLikePhoneSearch(searchTerm)) return null;
  return normalisePhoneNumber(searchTerm, region);
};
